/**
 * thermalProcess.ts - Moduł obliczeń procesów termicznych
 * PlexiSystem Engineering Module
 */

import { MaterialKey } from '../constants/materials';
import {
  FORMING,
  calculateHeatingTime,
  calculateTotalProcessTime,
  calculateMinBendRadius,
  calculateThermalExpansion
} from '../constants/forming';

export interface ThermalInput {
  material: MaterialKey;
  thickness_mm: number;
  process: 'bend' | 'thermo';
  dimensions?: {
    length_mm?: number;
    width_mm?: number;
    bend_radius_mm?: number;
  };
  temperature_delta_C?: number;
}

export interface ThermalResult {
  temperatures: {
    recommended_C: [number, number];
    selected_C: number;
  };
  timing: {
    heating_min: number;
    cooling_min: number;
    total_min: number;
  };
  expansion: {
    deltaL_mm?: number;
    deltaW_mm?: number;
    compensation_needed: boolean;
  };
  bend_params?: {
    min_radius_mm: number;
    actual_radius_mm?: number;
    radius_ok: boolean;
  };
  process_schedule: string[];
  warnings: string[];
  tips: string[];
}

/**
 * Główna funkcja planowania procesu termicznego
 */
export function planThermalProcess(input: ThermalInput): ThermalResult {
  const forming = FORMING[input.material];
  
  // Temperatury
  const temp_range = input.process === 'bend' ? forming.bend_C : forming.thermo_C;
  const selected_temp = (temp_range[0] + temp_range[1]) / 2; // Środek zakresu
  
  // Czasy
  const timing = calculateTotalProcessTime(
    input.material,
    input.thickness_mm,
    input.process,
    true // Chłodzenie aktywne
  );
  
  // Rozszerzalność termiczna
  let expansion: ThermalResult['expansion'] = {
    compensation_needed: false
  };
  
  if (input.dimensions?.length_mm && input.temperature_delta_C) {
    expansion.deltaL_mm = calculateThermalExpansion(
      input.material,
      input.dimensions.length_mm,
      input.temperature_delta_C
    );
    
    if (input.dimensions.width_mm) {
      expansion.deltaW_mm = calculateThermalExpansion(
        input.material,
        input.dimensions.width_mm,
        input.temperature_delta_C
      );
    }
    
    expansion.compensation_needed = 
      (expansion.deltaL_mm && Math.abs(expansion.deltaL_mm) > 0.5) ||
      (expansion.deltaW_mm && Math.abs(expansion.deltaW_mm) > 0.5);
  }
  
  // Parametry gięcia
  let bend_params: ThermalResult['bend_params'] | undefined;
  
  if (input.process === 'bend') {
    const min_radius = calculateMinBendRadius(input.material, input.thickness_mm);
    bend_params = {
      min_radius_mm: min_radius,
      actual_radius_mm: input.dimensions?.bend_radius_mm,
      radius_ok: input.dimensions?.bend_radius_mm 
        ? input.dimensions.bend_radius_mm >= min_radius 
        : true
    };
  }
  
  // Harmonogram procesu
  const schedule = generateProcessSchedule(
    input.material,
    input.process,
    selected_temp,
    timing
  );
  
  // Ostrzeżenia i wskazówki
  const warnings: string[] = [];
  const tips: string[] = [];
  
  // Dodaj ostrzeżenia specyficzne dla materiału
  if (forming.notes) {
    tips.push(...forming.notes);
  }
  
  // Ostrzeżenia o temperaturze
  if (input.process === 'thermo' && selected_temp > temp_range[1] - 10) {
    warnings.push('Temperatura blisko maksimum - ryzyko degradacji materiału');
  }
  
  // Ostrzeżenia o promieniu gięcia
  if (bend_params && !bend_params.radius_ok) {
    warnings.push(`Promień gięcia za mały! Min: ${bend_params.min_radius_mm}mm`);
  }
  
  // Ostrzeżenia o grubości
  if (input.thickness_mm > 10) {
    warnings.push('Grubość >10mm - rozważ dłuższe nagrzewanie lub wyżarzanie');
  }
  
  // Wskazówki procesowe
  if (input.material === 'PMMA' && input.thickness_mm > 5) {
    tips.push('Rozważ relaksację naprężeń 80-90°C przez 2-4h po formowaniu');
  }
  
  if (input.material === 'PETG') {
    tips.push('Nie używaj wody do chłodzenia - ryzyko pęknięć');
  }
  
  if (expansion.compensation_needed) {
    tips.push(`Uwzględnij rozszerzalność: ΔL=${expansion.deltaL_mm?.toFixed(1)}mm`);
  }
  
  return {
    temperatures: {
      recommended_C: temp_range,
      selected_C: selected_temp
    },
    timing,
    expansion,
    bend_params,
    process_schedule: schedule,
    warnings,
    tips
  };
}

/**
 * Generuje harmonogram procesu
 */
function generateProcessSchedule(
  material: MaterialKey,
  process: 'bend' | 'thermo',
  temperature: number,
  timing: { heating: number; cooling: number; total: number }
): string[] {
  const schedule: string[] = [];
  
  // Przygotowanie
  schedule.push('00:00 - Przygotowanie materiału');
  schedule.push('- Sprawdź czystość powierzchni');
  schedule.push('- Usuń folię ochronną jeśli jest');
  schedule.push('- Oznacz linie gięcia/formowania');
  
  // Nagrzewanie
  let currentTime = 5; // 5 minut na przygotowanie
  schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Start nagrzewania`);
  schedule.push(`- Ustaw temperaturę: ${temperature}°C`);
  schedule.push(`- Nagrzewanie równomierne z obu stron`);
  
  // Kontrola w połowie nagrzewania
  const halfHeating = Math.floor(timing.heating / 2);
  currentTime += halfHeating;
  schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Kontrola temperatury`);
  schedule.push(`- Sprawdź równomierność nagrzania`);
  schedule.push(`- Obróć element jeśli potrzeba`);
  
  // Koniec nagrzewania
  currentTime = 5 + timing.heating;
  schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Koniec nagrzewania`);
  schedule.push(`- Materiał osiągnął ${temperature}°C`);
  
  // Formowanie
  if (process === 'bend') {
    schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Gięcie`);
    schedule.push(`- Przenieś szybko do formy`);
    schedule.push(`- Wykonaj gięcie w ciągu 30 sekund`);
    schedule.push(`- Przytrzymaj w pozycji`);
  } else {
    schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Termoformowanie`);
    schedule.push(`- Przenieś na formę`);
    schedule.push(`- Zastosuj próżnię/ciśnienie`);
    schedule.push(`- Utrzymuj parametry formowania`);
  }
  
  // Chłodzenie
  currentTime += 2; // 2 minuty na formowanie
  schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Start chłodzenia`);
  schedule.push(`- Chłodzenie aktywne wentylatorem`);
  schedule.push(`- Utrzymuj element w formie`);
  
  // Koniec chłodzenia
  currentTime += timing.cooling;
  schedule.push(`00:${currentTime.toString().padStart(2, '0')} - Koniec procesu`);
  schedule.push(`- Temperatura <40°C`);
  schedule.push(`- Wyjmij element z formy`);
  schedule.push(`- Kontrola jakości`);
  
  // Opcjonalne wyżarzanie
  if (material === 'PMMA' || material === 'PC') {
    schedule.push('');
    schedule.push('OPCJONALNIE - Wyżarzanie (relaksacja naprężeń):');
    schedule.push('- Temperatura: 80-90°C');
    schedule.push('- Czas: 2-4 godziny');
    schedule.push('- Chłodzenie powolne');
  }
  
  return schedule;
}

/**
 * Oblicza parametry dla gięcia wielokrotnego
 */
export function calculateMultiBendParameters(
  material: MaterialKey,
  thickness_mm: number,
  bends: Array<{ angle_deg: number; radius_mm: number }>
): {
  total_time_min: number;
  sequence: string[];
  springback_compensation: number[];
} {
  const minRadius = calculateMinBendRadius(material, thickness_mm);
  const baseTime = calculateHeatingTime(material, thickness_mm, 'bend');
  
  const sequence: string[] = [];
  const springback_compensation: number[] = [];
  let totalTime = 0;
  
  bends.forEach((bend, index) => {
    // Sprawdź promień
    if (bend.radius_mm < minRadius) {
      sequence.push(`Gięcie ${index + 1}: BŁĄD - promień za mały (min ${minRadius}mm)`);
    } else {
      // Czas dla każdego gięcia
      const bendTime = index === 0 ? baseTime : baseTime * 0.7; // Kolejne gięcia szybsze
      totalTime += bendTime;
      
      // Kompensacja sprężynowania (zależy od kąta)
      const compensation = bend.angle_deg * 0.05; // ~5% kompensacji
      springback_compensation.push(compensation);
      
      sequence.push(`Gięcie ${index + 1}: ${bend.angle_deg}° (R${bend.radius_mm}mm) - ${bendTime}min`);
    }
  });
  
  return {
    total_time_min: totalTime,
    sequence,
    springback_compensation
  };
}

/**
 * Sprawdza możliwość termoformowania
 */
export function checkThermoformingFeasibility(
  material: MaterialKey,
  thickness_mm: number,
  draw_ratio: number
): {
  feasible: boolean;
  max_draw_ratio: number;
  recommendations: string[];
} {
  // Maksymalne współczynniki wyciągania
  const maxDrawRatios: Record<MaterialKey, number> = {
    PETG: 3.0,    // Najlepszy do głębokiego tłoczenia
    PMMA: 2.0,
    PC: 2.5,
    HIPS: 2.8,
    PVC: 2.2,
    ABS: 2.5
  };
  
  const maxRatio = maxDrawRatios[material];
  const feasible = draw_ratio <= maxRatio;
  
  const recommendations: string[] = [];
  
  if (!feasible) {
    recommendations.push(`Współczynnik wyciągania ${draw_ratio} za wysoki (max ${maxRatio})`);
    recommendations.push('Rozważ zmianę geometrii lub materiału');
    
    if (material !== 'PETG') {
      recommendations.push('PETG ma najlepsze właściwości do głębokiego tłoczenia');
    }
  }
  
  if (thickness_mm > 6) {
    recommendations.push('Grubość >6mm może wymagać dłuższego nagrzewania');
  }
  
  if (draw_ratio > 2) {
    recommendations.push('Zastosuj stopniowe formowanie lub podgrzewanie formy');
  }
  
  return {
    feasible,
    max_draw_ratio: maxRatio,
    recommendations
  };
}
